import {
  Gavel,
  Building2,
  Trophy,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  type Bid,
  BID_STATUS_LABELS,
  BID_STATUS_COLORS,
} from '@/features/procurement/types';

interface BidCardProps {
  readonly bid: Bid;
  readonly onAccept?: (bidId: string) => void;
  readonly onReject?: (bidId: string) => void;
  readonly onViewDetails?: (bidId: string) => void;
}

export function BidCard({
  bid, onAccept, onReject, onViewDetails 
}: BidCardProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: bid.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const isUnderReview = bid.status === 'under_review';
  const isSubmitted = bid.status === 'submitted';
  const canReview = isUnderReview || isSubmitted;

  return (
    <Card className={`hover:shadow-md transition-shadow ${bid.isLowestBid ? 'ring-2 ring-green-500' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              bid.isLowestBid ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              {bid.isLowestBid ? (
                <Trophy className="h-5 w-5 text-green-600" />
              ) : (
                <Gavel className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <CardTitle className="text-base">{bid.supplierName}</CardTitle>
              <p className="text-sm text-muted-foreground">{bid.eventTitle}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {bid.rank && (
              <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                Rank #{bid.rank}
              </span>
            )}
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${BID_STATUS_COLORS[bid.status]}`}>
              {BID_STATUS_LABELS[bid.status]}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className={`rounded-lg p-3 ${bid.isLowestBid ? 'bg-green-50 border border-green-200' : 'bg-muted/50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total Bid</p>
              <p className={`text-lg font-bold ${bid.isLowestBid ? 'text-green-600' : ''}`}>
                {formatCurrency(bid.total)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Line Items</p>
              <p className="font-semibold">{bid.lines.length}</p>
            </div>
          </div>
          {bid.isLowestBid && (
            <div className="mt-2 flex items-center gap-1 text-green-600">
              <Trophy className="h-4 w-4" />
              <span className="text-xs font-medium">Lowest Bid</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1">
            <Building2 className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">{bid.eventNumber}</span>
          </div>
          {bid.submittedAt && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-muted-foreground" />
              <span>{formatDate(bid.submittedAt)}</span>
            </div>
          )}
          {bid.validUntil && (
            <div className="flex items-center gap-1 col-span-2">
              <Calendar className="h-3 w-3 text-muted-foreground" />
              <span>Valid until: {formatDate(bid.validUntil)}</span>
            </div>
          )}
        </div>

        {bid.deliveryTerms && (
          <div className="rounded-lg bg-muted/50 p-2">
            <p className="text-xs text-muted-foreground">Delivery Terms</p>
            <p className="text-sm">{bid.deliveryTerms}</p>
          </div>
        )}

        {bid.status === 'accepted' && bid.reviewedByName && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-green-800">
                  Accepted by {bid.reviewedByName}
                </p>
                {bid.reviewedAt && (
                  <p className="text-xs text-green-700">{formatDate(bid.reviewedAt)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {bid.status === 'rejected' && bid.reviewedByName && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3">
            <div className="flex items-start gap-2">
              <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-red-800">
                  Rejected by {bid.reviewedByName}
                </p>
                {bid.reviewedAt && (
                  <p className="text-xs text-red-700">{formatDate(bid.reviewedAt)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onViewDetails?.(bid.bidId)}
          >
            View Details
          </Button>
          {canReview && onAccept && onReject && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => onReject(bid.bidId)}
              >
                Reject
              </Button>
              <Button size="sm" onClick={() => onAccept(bid.bidId)}>
                <CheckCircle className="mr-1 h-4 w-4" />
                Accept
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
